import { usePostHog } from '@posthog/react';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { capture } from '../analytics/posthog';
import ApiClient from '../config/ApiClient';
import FinalReflectionForm, { FinalReflectionData } from '../components/survey/FinalReflectionForm';

/**
 * Standalone page for the optional post-assessment Final Reflection.
 * Route: /assignment/:id/final-reflections (:id = PaymentAssignments id).
 * Reached by redirect from AssessmentScreen after the Post Assessment is submitted.
 */
const FinalReflection: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const assignmentId = id ? parseInt(id, 10) : null;
    const navigate = useNavigate();
    const posthog = usePostHog();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (data: FinalReflectionData) => {
        if (!assignmentId) {
            setError('Missing assignment. Please return to your dashboard.');
            return;
        }
        setIsSubmitting(true);
        setError(null);
        try {
            const payload: any = { assignment_id: assignmentId };
            if (typeof data.satisfaction_rating === 'number') {
                payload.satisfaction_rating = data.satisfaction_rating;
            }
            if (typeof data.reflection_text === 'string') {
                payload.reflection_text = data.reflection_text;
            }
            const result = await ApiClient.post('/api/final-reflections/', payload);
            if (!result.success) {
                throw new Error(result.error || 'Failed to submit final reflection');
            }

            capture(posthog, 'final_reflection', {
                assignment_id: assignmentId,
                satisfaction_rating: data.satisfaction_rating ?? null,
                has_reflection_text: !!(data.reflection_text && data.reflection_text.trim().length > 0),
            });

            navigate('/dashboard');
        } catch (err: any) {
            console.error('Error submitting final reflection:', err);
            setError('Could not save your feedback. Please try again or skip.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSkip = () => {
        navigate('/dashboard');
    };

    return (
        <FinalReflectionForm
            onSubmit={handleSubmit}
            onSkip={handleSkip}
            loading={isSubmitting}
            error={error}
        />
    );
};

export default FinalReflection;
